import { gsap,Power0 } from 'gsap';
import { StageLayer } from '@nolimitcity/slot-launcher/bin/StageLayer';
import { ISequencePlay, Sequence } from '../../../core/displayobject/Sequence';
import { Helper } from '../../../core/utils/Helper';
import { SpinePlayConfig, SpinePlayer } from '../../../core/displayobject/SpinePlayer';
import { BLEND_MODES } from 'pixi.js';
import { BeheadedEngineManager } from '../../reels/BeheadedEngineManager';
import { Beheaded } from '../../Beheaded';

export interface OverlayItem {
    item: PIXI.DisplayObject;
    timeline: gsap.core.Timeline;
}

export class OverlayAnimation extends PIXI.Container implements StageLayer {

    constructor() {
        super();
        this.onResize();
    }

    private fetchSWSpinePath(i:number):string{
        const skin = ["green","purple","red"][i];
        return `soul_wild_${skin}`;
    }

    private introSpines:SpinePlayer[];
    
    public PlayIntroSpine():gsap.core.Timeline{
        this.introSpines = [];

        const spine = this.addChild(new SpinePlayer(this.fetchSWSpinePath(0)));
        const spine2 = this.addChild(new SpinePlayer(this.fetchSWSpinePath(1)));
        const spine3 = this.addChild(new SpinePlayer(this.fetchSWSpinePath(2)));

        this.introSpines.push(spine)
        spine.position.set(200,360);

        this.introSpines.push(spine2)
        spine2.position.set(520,360);

        this.introSpines.push(spine3)
        spine3.position.set(360,210);

        const tl = gsap.timeline();
        const appearDurr = spine3.fetchDuration("appear");
        
        this.introSpines.forEach((element,i) => {
            element.scale.set(3);
            tl.add(() => {
                element.play({animation:"appear", showOnEnd:true, duration:1,onComplete:() => {
                    element.play({animation:"loop",loop:true});
                }});
            }, i * appearDurr + 0.3); 
        });
        const totDurr = tl.totalDuration() + appearDurr + 0.2;
        this.introSpines.forEach((element,i) => {
            element.scale.set(3);
            tl.add(() => { element.play({animation:"BigWin", loop:true})}, totDurr); 
        });

        this.sortChildren();
        return tl;
    }

    public endFade(durr:number):gsap.core.Timeline{
        const tl = gsap.timeline({onComplete:() => {
            introGFX.destroy();
        }})
        const introGFX = this.createGraphic(2000,2000,0x000000);

        introGFX.position.set(360,360);
        introGFX.zIndex = -2;
        const toScale:number = 1.0015;

        const toDurr = durr/5;
        const fromDurr = durr - toDurr;
        
        tl.to(
            this.parent.scale,
            { x: toScale, y: toScale, duration: toDurr,ease:"Power0.easeNone" }
        ,0);
        tl.to(
            this.parent.scale,
            { x: 1, y: 1, duration: fromDurr,ease:"Power0.easeNone" }
        ,toDurr);

        tl.fromTo(introGFX,{alpha:0},{alpha:0.15,duration:toDurr},0);
        tl.to(introGFX,{alpha:0,duration:fromDurr},toDurr);
        // tl.to(introGFX,{alpha:0,duration:durr/2},durr/2);

        return tl;
    }

    public bounceScreen(durr:number,scaleVal:number = 1.02):gsap.core.Timeline{
        const tl = gsap.timeline({onComplete:() => {
        }})

        tl.fromTo(
            this.parent.scale,
            { x: scaleVal, y: scaleVal },
            { x: 1, y: 1, duration: durr }
        ,0);

        return tl;
    }

    public playIntroDarkFade(durr:number):gsap.core.Timeline{
        const introGFX = this.createGraphic(2000,2000,0x000000);

        introGFX.position.set(360,360);
        introGFX.zIndex = -2;

        const tl = gsap.timeline({onComplete:() => {
            introGFX.destroy();
            this.introSpines.forEach(e => e.destroy());
        }})

        tl.to(introGFX,{alpha:0, duration:durr},0);
        this.introSpines.forEach((s,i) => {
            tl.to(s.scale,{x:1,y:1, duration:durr},0);
        });

        const startMove = tl.duration();

        this.introSpines.forEach((s,i) => {
            const moveTo = Helper.copyLocalPosition(s,Beheaded.souldWildController.SWIndicators[i]);
            tl.to(s,{x:moveTo.x,y:moveTo.y, duration:durr, ease: "Back.easeIn"},startMove);
            tl.add(() => {s.play({animation:"remove"})})
        });

        tl.to(this,{duration:this.introSpines[0].fetchDuration("remove")})

        this.sortChildren();

        return tl;
    }

    private createGraphic(sizeX: number = 100, sizeY?: number, color?: number): PIXI.Graphics {
        if (!sizeY) sizeY = sizeX;
        return this.addChild(
            Helper.createBasicGraphic(color != undefined ? color : 0xffffff, 1, -sizeX * 0.5, -sizeY * 0.5, sizeX, sizeY)
        );
    }

    public playStandAloneSequence(texture: PIXI.Texture[], conf: ISequencePlay = {}, layer?: PIXI.Container): OverlayItem {
        const item: Sequence = layer
            ? layer.addChild(Sequence.createSequence(texture))
            : this.addChild(Sequence.createSequence(texture));

        const tl: gsap.core.Timeline = Helper.temporaryItemTl(item);

        tl.add(item.play(conf));

        return { item: item, timeline: tl };
    }

    public addChild<TChildren extends PIXI.DisplayObject[]>(...child: TChildren): TChildren[0] {
        super.addChild(...child);
        this.sortChildren();
        return child[0];
    }

    public playStandAloneSpine(path: string, conf: SpinePlayConfig, layer?: PIXI.Container): OverlayItem {
        const item: SpinePlayer = layer ? layer.addChild(new SpinePlayer(path)) : this.addChild(new SpinePlayer(path));
        const tl: gsap.core.Timeline = Helper.temporaryItemTl(item);

        tl.add(item.play(conf));

        return { item: item, timeline: tl };
    }

    public playSpine(
        target: PIXI.DisplayObject,
        path: string,
        conf: SpinePlayConfig,
        stay: boolean = false,
        cont?: PIXI.Container,
        initVisible: boolean = true
    ): OverlayItem {
        const item: SpinePlayer = cont
            ? cont.addChild(new SpinePlayer(path, initVisible))
            : this.addChild(new SpinePlayer(path, initVisible));
        const tl: gsap.core.Timeline = stay
            ? new gsap.core.Timeline({
                onStart:() => { item.position = Helper.copyLocalPosition(item, target); },
                onUpdate: () => {
                    if (!target) {
                        tl.kill();
                        item.destroy();
                    }
                    conf.onUpdate ? conf.onUpdate() : undefined;
                  },
              })
            : this.createItemTlToFollow(target, item);

        item.position = Helper.copyLocalPosition(item, target);
        tl.add(item.play(conf));

        return { item: item, timeline: tl };
    }

    public playSequence(
        target: PIXI.DisplayObject,
        texture: PIXI.Texture[],
        conf: ISequencePlay = {},
        layer?: PIXI.Container
    ): OverlayItem {
        const item: Sequence = layer
            ? layer.addChild(Sequence.createSequence(texture))
            : this.addChild(Sequence.createSequence(texture));

        const tl: gsap.core.Timeline = this.createItemTlToFollow(target, item);

        tl.add(item.play(conf));

        return { item: item, timeline: tl };
    }

    public flashGFX(
        target: PIXI.DisplayObject,
        sizeX?: number,
        sizeY?: number,
        durr: number = 0.5,
        color?: number
    ): OverlayItem {
        const item: PIXI.DisplayObject = this.createGraphic(sizeX, sizeY, color);

        const tl: gsap.core.Timeline = this.createItemTlToFollow(target, item);

        tl.fromTo(item, { alpha: 0 }, { alpha: 1, duration: durr / 2 });
        tl.to(item, { alpha: 0, duration: durr / 2 });
        return { item: item, timeline: tl };
    }

    public useItemAsOverlay(target: PIXI.DisplayObject, item: PIXI.DisplayObject): OverlayItem {
        const tl: gsap.core.Timeline = this.createItemTlToFollow(target, item);
        this.addChild(item);
        return { item: item, timeline: tl };
    }

    private createItemTlToFollow(target: PIXI.DisplayObject, item: PIXI.DisplayObject): gsap.core.Timeline {
        const tl = new gsap.core.Timeline({
            onUpdate: () => {
                if (!target || !target.parent) {
                    remove();
                } else if (item.transform) {
                    item.position = Helper.copyLocalPosition(item, target);
                }
            },
            onComplete: () => remove(),
        });

        item.position = Helper.copyLocalPosition(item, target);

        function remove() {
            tl.kill();
            item.transform && item.destroy();
            target = undefined!;
        }

        return tl;
    }

    public removeNonUsedAnimations(): void {
        this.children.forEach((s) => {
            if (s.name != 'mask') s.destroy();
        });
    }

    public flashMystery(reel: number, row: number) {
        const toPosition: PIXI.Point = Beheaded.reelEngineManager.getSymPos(reel, row);

        let sp: Sequence = this.addChild(new Sequence({ texturePath: 'M/4/M_normal' }));
        sp.blendMode = BLEND_MODES.ADD;
        sp.position = toPosition;
        sp.anchor.set(0.5, 0.5);

        gsap.fromTo(sp, { alpha: 1 }, { alpha: 0, duration: 2 }).eventCallback('onComplete', () => {
            sp.destroy();
        });
    }

    public onResize(): void {
        this.scale = BeheadedEngineManager.dim.scale;
    }
}
